import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "@phosphor-icons/react";
import PromoCountdown from "./PromoCountdown";

const PathCard = ({ data, variant }) => {
  const isPremium = variant === "premium";
  const features = data.features;

  return (
    <article
      data-testid={`path-card-${variant}`}
      className={`relative rounded-3xl p-8 lg:p-10 flex flex-col h-full transition-all ${
        isPremium
          ? "adonis-gradient-card lg:scale-[1.02] shadow-[0_30px_80px_-20px_rgba(123,97,255,0.25)]"
          : "bg-white border border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
            isPremium
              ? "adonis-gradient text-white"
              : "bg-slate-100 text-slate-700"
          }`}
        >
          {data.tag}
        </span>
      </div>

      <h3 className="mt-6 font-heading text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-slate-900">
        {data.title}
      </h3>

      <p className="mt-4 text-sm leading-relaxed text-slate-600">
        {data.desc}
      </p>

      <div className="mt-8 pt-8 border-t border-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {data.priceLabel}
        </p>
        <div className="mt-3 flex items-baseline gap-3 flex-wrap">
          {data.originalPrice && (
            <p
              data-testid={`path-${variant}-price-original`}
              className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-slate-400 line-through decoration-2 decoration-red-500/70"
            >
              {data.originalPrice}
            </p>
          )}
          <p
            data-testid={`path-${variant}-price`}
            className={`font-heading text-4xl lg:text-5xl font-bold tracking-tight ${
              data.originalPrice
                ? "text-red-600"
                : isPremium
                  ? "adonis-gradient-text"
                  : "text-slate-900"
            }`}
          >
            {data.price}
          </p>
          <p className="text-sm text-slate-500">{data.priceNote}</p>
        </div>
        {data.promoTag && (
          <span
            data-testid={`path-${variant}-promo-tag`}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-red-700 border border-red-200"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
            {data.promoTag}
          </span>
        )}
      </div>

      <ul className="mt-8 space-y-3 flex-1">
        {features.map((f, i) => (
          <li
            key={i}
            data-testid={`path-${variant}-feature-${i}`}
            className="flex items-start gap-3 text-sm leading-relaxed text-slate-700"
          >
            <span
              className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                isPremium ? "adonis-gradient" : "bg-slate-900"
              }`}
            >
              <Check size={12} weight="bold" className="text-white" />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-10">
        <Link
          to={data.ctaTo}
          data-testid={`path-cta-${variant}`}
          className={`group inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 ${
            isPremium
              ? "adonis-gradient text-white hover:shadow-xl hover:shadow-[#7B61FF]/30"
              : "bg-slate-900 text-white hover:shadow-xl hover:shadow-slate-900/20"
          }`}
        >
          {data.cta}
          <ArrowRight
            size={16}
            weight="bold"
            className="transition-transform group-hover:translate-x-1"
          />
        </Link>
      </div>
    </article>
  );
};

export const TwoPaths = () => {
  const { t } = useTranslation();

  const path1 = {
    tag: t("programme.paths.p1_tag"),
    title: t("programme.paths.p1_title"),
    desc: t("programme.paths.p1_desc"),
    features: t("programme.paths.p1_features", { returnObjects: true }),
    priceLabel: t("programme.paths.p1_price_label"),
    price: t("programme.paths.p1_price"),
    originalPrice: t("programme.paths.p1_price_original", {
      defaultValue: "",
    }) || null,
    promoTag: t("programme.paths.promo_tag", { defaultValue: "" }) || null,
    priceNote: t("programme.paths.p1_price_note"),
    cta: t("programme.paths.p1_cta"),
    ctaTo: "/contact",
  };

  const path2 = {
    tag: t("programme.paths.p2_tag"),
    title: t("programme.paths.p2_title"),
    desc: t("programme.paths.p2_desc"),
    features: t("programme.paths.p2_features", { returnObjects: true }),
    priceLabel: t("programme.paths.p2_price_label"),
    price: t("programme.paths.p2_price"),
    originalPrice: t("programme.paths.p2_price_original", {
      defaultValue: "",
    }) || null,
    promoTag: t("programme.paths.promo_tag", { defaultValue: "" }) || null,
    priceNote: t("programme.paths.p2_price_note"),
    cta: t("programme.paths.p2_cta"),
    ctaTo: "/contact",
  };

  return (
    <section data-testid="programme-paths" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("programme.paths.kicker")}
          </p>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("programme.paths.title")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            {t("programme.paths.subtitle")}
          </p>
        </div>

        <PromoCountdown />

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <PathCard data={path1} variant="standard" />
          <PathCard data={path2} variant="premium" />
        </div>

        <p
          data-testid="paths-footnote"
          className="mt-10 text-center text-xs text-slate-500 max-w-2xl mx-auto"
        >
          {t("programme.paths.fork_footnote")}
        </p>
      </div>
    </section>
  );
};

export default TwoPaths;
